// logic/integrationlogic/hooks/useInvalidCredentialLock.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { secureStorage } from '../../utils/secureStorage';
import logger from '../../services/logging/logger';

/**
 * Hook to manage invalid credential lock state
 * When credentials are invalid, user is locked to organization-settings page
 * Uses encrypted storage for sensitive data (OWASP A02 mitigation)
 */
export const useInvalidCredentialLock = () => {
  const [isLocked, setIsLocked] = useState(false);
  const [invalidIntegrations, setInvalidIntegrations] = useState([]);
  // Where the user must go to fix the invalid credentials — the tenant settings page for
  // tenant-scoped integrations (Cognito), or a specific division's settings tab for
  // division-scoped ones (AWS, Bedrock, Atlassian, XRAY, Test Engine). Defaults to the
  // tenant page for backward compatibility with any pre-existing stored lock.
  const [lockedPath, setLockedPath] = useState('/organization-settings');
  const navigate = useNavigate();
  const location = useLocation();

  // Check encrypted storage on mount and location change
  useEffect(() => {
    const check = async () => {
      const invalidFromStorage = await secureStorage.getEncrypted('invalidIntegrations');

      if (invalidFromStorage) {
        try {
          if (Array.isArray(invalidFromStorage) && invalidFromStorage.length > 0) {
            const storedPath = await secureStorage.getEncrypted('invalidIntegrationsRedirectPath');
            const redirectPath = storedPath || '/organization-settings';

            setIsLocked(true);
            setInvalidIntegrations(invalidFromStorage);
            setLockedPath(redirectPath);

            logger.info('Invalid credentials detected from encrypted storage', {
              count: invalidFromStorage.length,
              redirectPath
            });

            // If not on the page that can fix these credentials, redirect immediately
            if (location.pathname !== redirectPath) {
              navigate(redirectPath, { replace: true });
            }
          }
        } catch (error) {
          logger.error('Error processing invalid integrations from encrypted storage', {
            error: error.message
          });
        }
      }
    };
    check();
  }, [location.pathname, navigate]);

  // Unlock when credentials are fixed
  const unlock = useCallback(() => {
    // Remove from encrypted storage
    secureStorage.removeEncrypted('invalidIntegrations');
    secureStorage.removeEncrypted('isAdminUser');
    secureStorage.removeEncrypted('invalidIntegrationsRedirectPath');
    setIsLocked(false);
    setInvalidIntegrations([]);
    setLockedPath('/organization-settings');
    logger.info('Invalid credential lock cleared');
  }, []);

  // Check if navigation should be blocked
  const shouldBlockNavigation = useCallback((targetPath) => {
    // Only block if locked and trying to leave the page that can fix the credentials
    return isLocked && targetPath !== lockedPath;
  }, [isLocked, lockedPath]);

  return {
    isLocked,
    invalidIntegrations,
    unlock,
    shouldBlockNavigation
  };
};